/* ============================================================
 * M26 Record Modal — 通用中奖记录弹框
 * ------------------------------------------------------------
 * PD（26-pd-promotion-lottery）与 autumn（autumn-promotion-lottery）
 * 在开启多 SKU 场景下共用同一套弹框 UI / 交互 / 样式，保证视觉
 * 与行为完全一致（PC + Mobile）。
 *
 * 设计要点：
 *   - 全局唯一：customElements.define("m26-record-modal", ...)
 *   - 调用方在按钮 / 自定义元素上挂载 <m26-record-modal>，
 *     并通过 data-* 属性传入 activityType、recordTitle、emptyText、
 *     emptyIconUrl、i18n JSON。点击弹框由模块内部托管。
 *   - 模块内部统一发出 pd-sku:open / autumn-sku:open 两个事件之一，
 *     由各自的 SKU 弹框组件监听；模块不耦合具体活动方。
 *   - 复用全局基础设施：M26Modal / m26Auth / m26Fetch / dayjs 等。
 *
 * 样式：apl-record__*（沿用 autumn 命名空间，避免双份 CSS）。
 *       assets/m26-record-modal.css 提供完整 PC + Mobile 布局。
 * ============================================================ */

(function () {
  "use strict";

  if (customElements.get("m26-record-modal")) return;

  /* ── 默认 i18n（与 autumn 一致） ── */
  const DEFAULT_I18N = {
    recordTitle: "My Winning Record",
    noWinningRecords:
      "You haven't drawn any prizes yet.\nTry your luck today!",
    edit: "Edit",
    claim: "Claim",
    claimed: "Claimed",
    copy: "Copy",
    copied: "Copied",
    contactMomcozy: "Contact Momcozy >",
    recordOrderName: "Order Number",
    goToMemberCenter: "Account>CozyCoins",
    goToGiftCardCenter: "Account>Coupons",
    am: "AM",
    pm: "PM",
    requestFailed: "Request failed",
    claimSuccess: "Claimed successfully.",
    claimFailed: "Claim failed.",
  };

  /* ── 工具：HTML 转义 ── */
  function esc(str) {
    if (str == null) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /* ── 工具：布尔归一 ── */
  function toBool(v) {
    if (v === true || v === "true" || v === 1 || v === "1") return true;
    if (v === false || v === "false" || v === 0 || v === "0") return false;
    return Boolean(v);
  }

  /* ── 工具：数字 ID 归一 ── */
  function normalizeId(value) {
    if (value == null || value === "") return "";
    const s = String(value);
    if (/^\d+$/.test(s)) return s;
    return s;
  }

  /* ── 工具：从奖品对象提取 productId ── */
  function prizeProductId(prize, fallback) {
    return normalizeId(
      prize.claimProductId ||
        prize.productId ||
        prize.productID ||
        prize.shopifyProductId ||
        (prize.raw &&
          (prize.raw.claimProductId ||
            prize.raw.productId ||
            prize.raw.productID ||
            prize.raw.shopifyProductId)) ||
        fallback ||
        "",
    );
  }

  /* ── 工具：会员中心 base URL（与 autumn 一致） ── */
  function accountBase() {
    try {
      const cfg = window.__MC__CONFIG__ || {};
      const base = cfg.accountSiteUrl || cfg.memberBase || "/account";
      return base;
    } catch (_) {
      return "/account";
    }
  }

  /* ── 工具：当前登录用户邮箱 ── */
  function currentUserEmail(m26Auth) {
    const m26 = m26Auth || {};
    let auth = null;
    try {
      if (typeof m26.syncFromCookie === "function") m26.syncFromCookie();
      if (typeof m26.get === "function") auth = m26.get();
    } catch (_) {}
    return (
      (auth && auth.user && auth.user.email) ||
      (window.__MOMCOZY_USER_INFO__ &&
        window.__MOMCOZY_USER_INFO__.email) ||
      ""
    );
  }

  /* ── 解析 m26 基础设施：优先 m26-mystery-box.js 全局，回退 PD / autumn IIFE 暴露的 *Local ── */
  function resolveM26() {
    const ModalClass =
      window.M26Modal || window.M26ModalLocal || null;
    /* m26Auth 与 m26AuthLocal 都是 { user, _cbs, onLogin } 形态，
       没有 .get() 方法；统一通过 .user 读取最新用户态。 */
    const auth =
      window.m26Auth ||
      window.m26AuthLocal ||
      (window.m26AuthLocal &&
        typeof window.m26AuthLocal.onLogin === "function" && {
          syncFromCookie: function () {
            /* m26-auth 不暴露独立 syncFromCookie，靠 LUTE:AUTH:LOGIN 事件触发；
               此处 no-op。 */
          },
          get: function () {
            return window.m26AuthLocal.user || null;
          },
        }) ||
      null;
    const fetcher =
      window.m26Fetch ||
      window.m26FetchLocal ||
      (typeof window.aplApiGet === "function"
        ? function (url) {
            const qIdx = url.indexOf("?");
            const path = qIdx >= 0 ? url.slice(0, qIdx) : url;
            const params = {};
            if (qIdx >= 0) {
              url
                .slice(qIdx + 1)
                .split("&")
                .forEach(function (kv) {
                  const i = kv.indexOf("=");
                  if (i > 0) {
                    params[decodeURIComponent(kv.slice(0, i))] =
                      decodeURIComponent(kv.slice(i + 1));
                  }
                });
            }
            return window.aplApiGet(path, params);
          }
        : null);
    const toaster =
      typeof window.m26Toast === "function"
        ? window.m26Toast
        : typeof window.m26ToastLocal === "function"
          ? window.m26ToastLocal
          : null;
    return { ModalClass, auth, fetcher, toaster };
  }

  /* ── 工具：当前登录用户邮箱（基于 resolveM26） ── */
  function getCurrentEmail(authObj) {
    const m26 = authObj || resolveM26().auth || {};
    let auth = null;
    try {
      if (typeof m26.syncFromCookie === "function") m26.syncFromCookie();
      if (typeof m26.get === "function") auth = m26.get();
      else if (m26 && typeof m26 === "object") auth = m26.user || null;
    } catch (_) {}
    return (
      (auth && auth.email) ||
      (window.__MOMCOZY_USER_INFO__ &&
        window.__MOMCOZY_USER_INFO__.email) ||
      ""
    );
  }

  /* ── 工具：时间格式化（市场时区） ── */
  function formatRecordTime(value, t) {
    if (value === null || value === undefined || value === "") return "";
    let ts = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(ts)) return String(value);
    if (ts < 1e12) ts *= 1000;
    try {
      const marketMap = {
        us: "America/New_York",
        ca: "America/Toronto",
        uk: "Europe/London",
        de: "Europe/Berlin",
        fr: "Europe/Paris",
        es: "Europe/Madrid",
        pl: "Europe/Warsaw",
        au: "Australia/Sydney",
        nz: "Pacific/Auckland",
      };
      const market =
        (window.__MC__CONFIG__ && window.__MC__CONFIG__.market) || "";
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: marketMap[String(market).toLowerCase()] || undefined,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).formatToParts(new Date(ts));
      const map = {};
      parts.forEach((part) => {
        map[part.type] = part.value;
      });
      let hour = Number(map.hour || 0);
      if (hour === 24) hour = 0;
      const period = hour >= 12 ? t("pm") : t("am");
      const hour12 = hour % 12 || 12;
      return `${map.month}/${map.day}/${map.year} ${hour12}:${map.minute || "00"}:${map.second || "00"} ${period}`;
    } catch (_) {
      return "";
    }
  }

  /* ── 工具：把 API 原始 record 归一为内部统一结构 ── */
  function normalizeRecord(raw) {
    const item = raw && typeof raw === "object" ? raw : {};
    return {
      raw: item,
      prizeType: Number(item.prizeType || item.type || 0),
      needSelectVariant: toBool(
        item.needSelectVariant == null ? item.multiVariant : item.needSelectVariant,
      ),
      activityUserPrizeCode:
        item.activityUserPrizeCode ||
        item.userPrizeCode ||
        item.prizeCode ||
        "",
      claimVariantId: normalizeId(
        item.claimVariantId || item.variantId || item.defaultVariantId,
      ),
      productId: prizeProductId(item, ""),
      productHandle: item.productHandle || item.handle || "",
      name: item.prizeName || item.name || "",
      image: item.prizeImg || item.prizeUrl || item.image || "",
      claimStatus: Number(item.claimStatus || 0),
      claimStatusDesc: item.claimStatusDesc || "",
      claimVariantTitle: item.claimVariantTitle || "",
      claimOrderName: item.claimOrderName || "",
      couponCode: item.couponCode || "",
      sendPrizeStatus: Number(item.sendPrizeStatus || 0),
      prizeTime: item.prizeTime || item.createTime || item.createdAt || "",
    };
  }

  /* ── 渲染：空状态 ── */
  function renderEmpty(ctx) {
    const iconUrl = ctx && ctx.emptyIconUrl ? ctx.emptyIconUrl : "";
    return `<div class="apl-record__state">${
      iconUrl
        ? `<img class="apl-record__state-icon" src="${esc(iconUrl)}" alt="" loading="lazy">`
        : ""
    }<p>${esc(ctx.t("noWinningRecords"))}</p></div>`;
  }

  /* ── 渲染：骨架屏（loading） ── */
  function renderSkeleton() {
    const isMobile = window.innerWidth <= 768;
    const count = isMobile ? 2 : 3;
    return Array.from({ length: count })
      .map(
        () =>
          `<div class="apl-record__item apl-record__item--skeleton" aria-hidden="true"><div class="apl-record__media"><span class="apl-record__skeleton apl-record__skeleton-img"></span></div><div class="apl-record__info"><span class="apl-record__skeleton apl-record__skeleton-title"></span><span class="apl-record__skeleton apl-record__skeleton-text"></span></div><div class="apl-record__status"><span class="apl-record__skeleton apl-record__skeleton-text"></span><span class="apl-record__skeleton apl-record__skeleton-short"></span></div><div class="apl-record__ops"><span class="apl-record__skeleton apl-record__skeleton-action"></span></div></div>`,
      )
      .join("");
  }

  /* ── 渲染：操作按钮（按 prizeType） ── */
  function renderActionByDefault(item, sid, index, defaultAction, ctx) {
    const { prizeType, couponCode } = item;
    const base = accountBase();
    let action = defaultAction;
    if (prizeType === 2) {
      action = `<a href="${base}/coins" class="apl-record__link apl-record__action">${ctx.t("goToMemberCenter")}</a>`;
    } else if ([3, 7].includes(prizeType)) {
      action = `<div class="apl-record__action__container"><span>${esc(couponCode)}</span><button type="button" class="apl-record__link apl-record__action" data-m26-section="${sid}" data-m26-record-action="copy" data-m26-record-index="${index}">${esc(ctx.t("copy"))}</button></div>`;
    } else if (prizeType === 5) {
      action = `<a href="${base}/coupon" class="apl-record__link apl-record__action">${ctx.t("goToGiftCardCenter")}</a>`;
    }
    return action;
  }

  /* ── 渲染：单个奖品条目（PC / Mobile 共用同一 markup，CSS 响应式） ── */
  function renderItem(item, index, ctx) {
    const sid = ctx.sid;
    const isMobile = window.innerWidth <= 768;
    const canEdit =
      item.prizeType === 1 && item.needSelectVariant && item.claimStatus === 10;
    const variantHtml = item.claimVariantTitle
      ? `<div class="apl-record__variant"><span>${esc(item.claimVariantTitle)}</span>${
          canEdit
            ? `<button type="button" class="apl-record__link apl-record__edit" data-m26-section="${sid}" data-m26-record-action="edit" data-m26-record-index="${index}">${esc(ctx.t("edit"))}</button>`
            : ""
        }</div>`
      : canEdit
        ? `<div class="apl-record__variant"><button type="button" class="apl-record__link apl-record__edit" data-m26-section="${sid}" data-m26-record-action="edit" data-m26-record-index="${index}">${esc(ctx.t("edit"))}</button></div>`
        : "";
    const statusHtml =
      item.claimStatusDesc || variantHtml
        ? `${item.claimStatusDesc ? `<p>${esc(item.claimStatusDesc)}</p>` : ""}${variantHtml}`
        : isMobile
          ? ""
          : `<p>-</p>`;
    let action = "-";
    if (item.claimStatus === 10) {
      action = `<button type="button" class="apl-record__link apl-record__action" data-m26-section="${sid}" data-m26-record-action="claim" data-m26-record-index="${index}">${esc(ctx.t("claim"))}</button>`;
      action = renderActionByDefault(item, sid, index, action, ctx);
    } else if (item.claimStatus === 20) {
      action = `<button type="button" class="apl-record__link apl-record__action is-disabled" disabled>${esc(ctx.t("claimed"))}</button>`;
      action = renderActionByDefault(item, sid, index, action, ctx);
    } else if (item.claimStatus === 30) {
      action = `<button type="button" class="apl-record__link apl-record__action" data-m26-section="${sid}" data-m26-record-action="contact" data-m26-record-index="${index}">${ctx.t("contactMomcozy")}</button>`;
    }
    const orderHtml =
      item.prizeType === 1 && item.claimStatus === 20
        ? `<p>${esc(ctx.t("recordOrderName"))}: ${esc(item.claimOrderName)}</p>`
        : "";
    const dateText = formatRecordTime(item.prizeTime, ctx.t);
    return `<div class="apl-record__item"><div class="apl-record__media">${
      item.image
        ? `<img src="${esc(item.image)}" alt="${esc(item.name)}" loading="lazy">`
        : ""
    }</div><div class="apl-record__info"><h3>${esc(item.name)}</h3>${
      dateText ? `<p>${esc(dateText)}</p>` : ""
    }</div><div class="apl-record__status">${statusHtml}${orderHtml}</div><div class="apl-record__ops">${action}</div></div>`;
  }

  /* ── 渲染：完整 modal 内容 ── */
  function renderModal(records, state, ctx) {
    const sid = ctx.sid;
    const email = ctx.email;
    let listHtml = "";
    if (state === "loading") {
      listHtml = renderSkeleton();
    } else if (!records || !records.length) {
      listHtml = renderEmpty(ctx);
    } else {
      listHtml = records.map((it, i) => renderItem(it, i, ctx)).join("");
    }
    return `<div class="apl-record"><h2 class="apl-record__title">${esc(ctx.t("recordTitle"))}</h2>${
      email ? `<p class="apl-record__email">(${esc(email)})</p>` : ""
    }<div class="apl-record__list">${listHtml}</div></div>`;
  }

  /* ── 全局辅助：fetch queryShopWinningRecord ── */
  async function fetchWinningRecords(activityType, m26Fetch) {
    const qs =
      "activityType=" +
      encodeURIComponent(activityType || "") +
      "&newRaffleFlow=true";
    const path = "/api/station/activity/raffle/queryShopWinningRecord?" + qs;
    const res = await m26Fetch(path);
    return res && res.recordBeans ? res.recordBeans : [];
  }

  /* ── 全局辅助：复制优惠码 ── */
  async function copyCoupon(code, button, ctx) {
    if (!code) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const input = document.createElement("textarea");
        input.value = code;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }
      /* 与 autumn 一致：直接把按钮文字换成 "Copied"，2s 后还原 */
      const prev = button.textContent;
      button.textContent = ctx.t("copied");
      ctx.toast(ctx.t("copied"), "success");
      setTimeout(() => {
        button.textContent = prev;
      }, 2000);
    } catch (_) {
      ctx.toast(ctx.t("claimFailed"), "error");
    }
  }

  /* ============================================================
   * <m26-record-modal> 自定义元素
   * ------------------------------------------------------------
   * 用法（PD / autumn 任一）：
   *   <m26-record-modal
   *     data-activity-type="pd_promotion_lottery"
   *     data-section-id="{{ section.id }}"
   *     data-record-empty-icon="..."
   *     data-sku-source="pd"   // 或 "autumn"
   *     data-i18n='{...}'
   *   >
   *     <button type="button" data-m26-record-trigger>...</button>
   *   </m26-record-modal>
   *
   * 监听：
   *   - 内部按钮 click：模块自己处理（open / claim / edit / contact / copy）
   *   - 外部事件：派发 <sku-source>-sku:open 唤起 SKU 弹框
   * ============================================================ */
  class M26RecordModal extends HTMLElement {
    static get DEFAULT_I18N() {
      return DEFAULT_I18N;
    }

    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._modal = null;
      this._records = [];
      this._recordItems = [];
      this._activityType =
        this.dataset.activityType || window.__MC_ACTIVITY_TYPE__ || "";
      this._skuSource = this.dataset.skuSource || "pd";
      this._sectionId = this.dataset.sectionId || "";
      this._i18nRaw = {};
      try {
        this._i18nRaw = this.dataset.i18n
          ? JSON.parse(this.dataset.i18n)
          : {};
      } catch (_) {
        this._i18nRaw = {};
      }
      this._i18n = Object.assign({}, DEFAULT_I18N, this._i18nRaw);

      this.addEventListener("click", this._onHostClick);
      document.addEventListener(
        this._skuSource + "-sku:close",
        this._onSkuClose,
      );
    }

    disconnectedCallback() {
      this.removeEventListener("click", this._onHostClick);
      document.removeEventListener(
        this._skuSource + "-sku:close",
        this._onSkuClose,
      );
      if (this._modal) {
        this._modal.destroy();
        this._modal = null;
      }
    }

    /* ── 用户点击容器内部（按钮触发） ── */
    _onHostClick = (e) => {
      const trigger = e.target.closest("[data-m26-record-trigger]");
      if (trigger) {
        e.stopPropagation();
        this._openModal();
      }
    };

    /* ── SKU 弹框关闭后：仅当 modal 处于被隐藏状态时恢复显示 ── */
    _onSkuClose = () => {
      if (this._modal && this._modal._boxEl) {
        const box = this._modal._boxEl;
        if (box.getAttribute("data-m26-hidden-by-sku") === "1") {
          box.classList.remove("m26-record-modal-hidden-by-sku");
          box.removeAttribute("data-m26-hidden-by-sku");
          box.removeAttribute("aria-hidden");
        }
      }
    };

    /* ── i18n 取值 ── */
    t(key) {
      return this._i18n[key] != null ? this._i18n[key] : DEFAULT_I18N[key] || "";
    }

    /* ── 渲染上下文 ── */
    _buildCtx() {
      const deps = resolveM26();
      const self = this;
      return {
        sid: this._sectionId,
        emptyIconUrl: this.dataset.recordEmptyIcon || "",
        t: (k) => this.t(k),
        email: getCurrentEmail(deps.auth),
        toast: (msg, type) => {
          if (deps.toaster) {
            try {
              deps.toaster(msg, type);
            } catch (_) {}
            return;
          }
          /* 简易 fallback toast */
          let toast = document.querySelector(".m26-toast");
          if (!toast) {
            toast = document.createElement("div");
            toast.className = "m26-toast";
            toast.style.cssText =
              "position:fixed;top:24px;left:50%;transform:translateX(-50%);background:#202025;color:#fff;padding:10px 18px;border-radius:8px;z-index:2000;font-size:14px;";
            toast.innerHTML =
              '<span data-m26-toast-content></span>';
            document.body.appendChild(toast);
          }
          toast.classList.toggle("m26-toast--error", type === "error");
          const contentEl = toast.querySelector("[data-m26-toast-content]");
          if (contentEl) contentEl.textContent = msg || "";
          toast.style.display = "block";
          clearTimeout(self._toastTimer);
          self._toastTimer = setTimeout(() => {
            toast.style.display = "none";
          }, 1500);
        },
      };
    }

    /* ── 打开弹框 ── */
    async _openModal() {
      const deps = resolveM26();
      const authObj = deps.auth || {};
      try {
        if (typeof authObj.syncFromCookie === "function")
          authObj.syncFromCookie();
      } catch (_) {}
      /* authObj 可能是 { user, _cbs, onLogin } 形态（来自 m26Auth / m26AuthLocal），
         也可能是带 get() 的包装；统一兼容。 */
      let auth = null;
      if (typeof authObj.get === "function") {
        auth = authObj.get();
      } else if (authObj && typeof authObj === "object") {
        auth = authObj.user || null;
      }
      if (!auth || !auth.email) {
        this._triggerLogin();
        return;
      }
      const ModalClass = deps.ModalClass;
      if (!ModalClass) return;
      if (!this._modal) {
        this._modal = new ModalClass("apl-modal apl-modal--record");
      }
      const ctx = this._buildCtx();
      this._ctx = ctx;
      this._modal.setContent(renderModal([], "loading", ctx));
      this._modal.show();
      const btn = this.querySelector("[data-m26-record-trigger]");
      if (btn) btn.setAttribute("aria-expanded", "true");
      setTimeout(() => {
        if (btn) btn.setAttribute("aria-expanded", "false");
      }, 400);
      try {
        const fetcher = deps.fetcher;
        if (!fetcher) throw new Error("fetch not available");
        const beans = await fetchWinningRecords(this._activityType, fetcher);
        this._records = beans;
        this._recordItems = beans.map((b) => normalizeRecord(b));
        this._modal.setContent(renderModal(this._recordItems, "ready", ctx));
        this._bindActions();
      } catch (err) {
        this._records = [];
        this._recordItems = [];
        this._modal.setContent(renderModal([], "empty", ctx));
        const msg =
          (err && (err.message || err.toString())) || this.t("requestFailed");
        ctx.toast(msg, "error");
      }
    }

    /* ── 绑定弹框内按钮事件 ── */
    _bindActions() {
      if (!this._modal || !this._modal._bodyEl) return;
      const root = this._modal._bodyEl;
      root.querySelectorAll("[data-m26-record-action]").forEach((btn) => {
        btn.addEventListener("click", (ev) => {
          ev.preventDefault();
          const action = btn.dataset.m26RecordAction;
          const index = Number(btn.dataset.m26RecordIndex);
          const item = this._recordItems[index];
          if (!item) return;
          if (action === "contact") this._handleContact();
          else if (action === "edit") this._handleEdit(item, btn);
          else if (action === "claim") this._handleClaim(item, btn);
          else if (action === "copy") copyCoupon(item.couponCode, btn, this._ctx);
        });
      });
    }

    _handleContact() {
      const launcher = document.querySelector("#launcher");
      if (launcher) launcher.style.zIndex = "100000";
      if (typeof window.zE === "function") window.zE("messenger", "open");
    }

    _handleEdit(item, btn) {
      /* 隐藏当前 record modal（保留 DOM） */
      if (this._modal && this._modal._boxEl) {
        const box = this._modal._boxEl;
        box.classList.add("m26-record-modal-hidden-by-sku");
        box.setAttribute("data-m26-hidden-by-sku", "1");
        box.setAttribute("aria-hidden", "true");
      }
      const detail = {
        prize: {
          productId: prizeProductId(item, ""),
          productHandle: item.productHandle || "",
          name: item.name || "",
          image: item.image || "",
          claimVariantId: item.claimVariantId || "",
          activityUserPrizeCode: item.activityUserPrizeCode || "",
          activityType: this._activityType || "",
          prizeType: item.prizeType,
          raw: item.raw,
        },
        trigger: btn,
        background:'transparent',
        source: "m26-record-modal",
        disappeared:true
      };
      document.dispatchEvent(
        new CustomEvent(this._skuSource + "-sku:open", { detail }),
      );
    }

    _handleClaim(item, btn) {
      btn.disabled = true;
      btn.classList.add("is-loading");
      btn.setAttribute("aria-busy", "true");
      const deps = resolveM26();
      const fetcher = deps.fetcher;
      let qs =
        "?activityType=" +
        encodeURIComponent(this._activityType || "") +
        "&activityUserPrizeCode=" +
        encodeURIComponent(item.activityUserPrizeCode || "");
      if (item.claimVariantId) {
        qs +=
          "&claimVariantId=" +
          encodeURIComponent(String(item.claimVariantId));
      }
      const path = "/api/station/activity/raffle/queryClaimParams" + qs;
      fetcher(path)
        .then((res) => {
          const checkoutUrl = res && (res.checkoutUrl || res.url);
          if (checkoutUrl) {
            if (this._modal) this._modal.close();
            window.location.href = checkoutUrl;
          } else {
            this._ctx.toast(this.t("claimFailed"), "error");
          }
        })
        .catch(() => {
          this._ctx.toast(this.t("claimFailed"), "error");
        })
        .finally(() => {
          btn.disabled = false;
          btn.classList.remove("is-loading");
          btn.removeAttribute("aria-busy");
        });
    }

    /* ── 唤起登录：派发统一事件，由登录组件监听 ── */
    _triggerLogin() {
      const detail = {
        activityType: this._activityType,
        source: "m26-record-modal",
      };
      document.dispatchEvent(
        new CustomEvent("m26:login-required", { detail }),
      );
      /* 兼容旧的 pdLoginForEl / m26LoginLocal */
      if (typeof window.pdLoginForEl === "function") {
        try {
          window.pdLoginForEl(this);
        } catch (_) {}
      } else if (typeof window.m26LoginLocal === "function") {
        try {
          window.m26LoginLocal();
        } catch (_) {}
      }
    }

    /* ── 公共：手动打开（供外部触发） ── */
    open() {
      return this._openModal();
    }

    close() {
      if (this._modal) this._modal.close();
    }
  }

  /* ── 暴露公共 API ── */
  window.M26RecordModal = M26RecordModal;
  window.M26RecordModalShared = {
    normalizeRecord,
    renderModal,
    renderItem,
    renderSkeleton,
    renderEmpty,
    renderActionByDefault,
    formatRecordTime,
    fetchWinningRecords,
    copyCoupon,
    esc,
    toBool,
    normalizeId,
    prizeProductId,
    accountBase,
    currentUserEmail,
    DEFAULT_I18N,
  };

  customElements.define("m26-record-modal", M26RecordModal);
})();